import type { BaseTemplateConfig } from "../schemas/template";

/**
 * Store shared untuk runtime state template aktif.
 *
 * Store ini sengaja dijaga tetap ringan:
 * - menyimpan config template aktif
 * - menyimpan derived state kecil lintas section
 * - tidak memegang orchestration fetch yang panjang
 *
 * Business flow yang lebih tebal tetap diletakkan di composable agar boundary
 * antara state holder dan orchestration tetap jelas.
 */
export const useTemplateStore = defineStore("template", () => {
  const { flavor } = useRuntimeConfig().public;
  const appConfig = useAppConfig();

  const config = ref<BaseTemplateConfig>(
    (appConfig?.template ? { ...appConfig.template } : {}) as BaseTemplateConfig,
  );

  const isRsvpDisabled = ref(false);

  const coupleName = computed(() => {
    const groomName = config.value.general.groom_nickname;
    const brideName = config.value.general.bride_nickname;

    return `${groomName} & ${brideName}`;
  });

  /**
   * Menimpa sebagian config aktif tanpa me-reset seluruh tree template.
   * Gunakan untuk sinkronisasi runtime, bukan untuk mengganti contract shape.
   *
   * @param newConfig Potongan config yang ingin digabung ke config aktif
   */
  function setTemplateConfig(newConfig: Partial<BaseTemplateConfig>) {
    config.value = {
      ...config.value,
      ...newConfig,
    };
  }

  /**
   * Menjaga preview tetap relevan ketika tanggal event mock sudah lewat.
   * Jika event preview berada di masa lalu, tanggal digeser ke hari Minggu terdekat berikutnya
   * sambil mempertahankan jam aslinya.
   *
   * @param eventDate Tanggal event opsional yang ingin dinormalisasi
   */
  function normalizeEventDateForPreview(eventDate?: string) {
    if (flavor !== "preview") {
      return;
    }

    const date = new Date(eventDate || config.value.general.date);
    const currentDate = new Date();
    const diffInSeconds = Math.floor((date.getTime() - currentDate.getTime()) / 1000);

    if (diffInSeconds <= 0) {
      const nextSunday = new Date();

      // Cari hari minggu berikutnya lalu pakai jam dari event aslinya.
      nextSunday.setDate(currentDate.getDate() + ((7 - currentDate.getDay()) % 7 || 7));
      nextSunday.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), 0);

      config.value.general.date = nextSunday.toISOString();
    }
  }

  /**
   * Menyimpan hasil evaluasi policy RSVP agar section/composable lain
   * dapat membaca status disabled dari satu sumber yang sama.
   *
   * @param value Nilai disabled RSVP terbaru
   */
  function setIsRsvpDisabled(value: boolean) {
    isRsvpDisabled.value = value;
  }

  return {
    template: computed(() => config.value),
    setTemplateConfig,
    normalizeEventDateForPreview,
    coupleName: computed(() => coupleName.value),

    // RSVP
    isRsvpDisabled: computed(() => isRsvpDisabled.value),
    setIsRsvpDisabled,
  };
});
